import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService, TradeAnalysisItem } from '../../services/api.service';
import { AnalysisDialogComponent, AnalysisFile } from '../../components/analysis-dialog/analysis-dialog.component';

@Component({
  selector: 'app-analyses',
  standalone: true,
  imports: [CommonModule, FormsModule, AnalysisDialogComponent],
  templateUrl: './analyses.component.html',
  styleUrls: ['./analyses.component.scss']
})
export class AnalysesComponent implements OnInit {
  analyses: TradeAnalysisItem[] = [];
  selectedAnalysis: TradeAnalysisItem | null = null;
  selectedJson: AnalysisFile | null = null;
  loading = false;
  message = '';
  symbolFilter = '';
  dateFrom = '';
  dateTo = '';

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    this.loading = true;
    this.message = '';
    try {
      const response = await firstValueFrom(this.api.getTradeAnalyses());
      this.analyses = response?.success ? response.items ?? [] : [];
      if (!response?.success) {
        this.message = response?.message || 'Analysen konnten nicht geladen werden.';
      }
    } catch (error) {
      this.analyses = [];
      this.message = 'Analysen konnten nicht vom Backend geladen werden.';
    } finally {
      this.loading = false;
    }
  }

  openAnalysis(analysis: TradeAnalysisItem): void {
    try {
      this.selectedAnalysis = analysis;
      this.selectedJson = JSON.parse(analysis.rawJson || '{}') as AnalysisFile;
    } catch {
      this.selectedAnalysis = analysis;
      this.selectedJson = { instrument: analysis.instrument ?? analysis.symbol ?? 'Analyse', varianten: [] };
    }
  }

  closeAnalysis(): void {
    this.selectedAnalysis = null;
    this.selectedJson = null;
  }

  rowTrack(_: number, item: TradeAnalysisItem): string {
    return item.id;
  }

  filteredAnalyses(): TradeAnalysisItem[] {
    const symbol = this.symbolFilter.trim().toLowerCase();
    const from = this.dateFrom ? new Date(`${this.dateFrom}T00:00:00`) : null;
    const to = this.dateTo ? new Date(`${this.dateTo}T23:59:59`) : null;

    return this.analyses.filter((item) => {
      const itemSymbol = this.displaySymbol(item).toLowerCase();
      if (symbol && !itemSymbol.includes(symbol)) {
        return false;
      }

      const analysisDate = this.analysisDateValue(item);
      if (from && (!analysisDate || analysisDate < from)) {
        return false;
      }
      if (to && (!analysisDate || analysisDate > to)) {
        return false;
      }
      return true;
    });
  }

  clearFilters(): void {
    this.symbolFilter = '';
    this.dateFrom = '';
    this.dateTo = '';
  }

  displaySymbol(item: TradeAnalysisItem): string {
    return item.symbol || item.instrument || '-';
  }

  private analysisDateValue(item: TradeAnalysisItem): Date | null {
    const value = item.analysisDateTime || item.analysisDate;
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
